'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { trackEvent, identifyUser } from '@/lib/mixpanel';

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const { signIn } = useAuth();
  const router = useRouter();

  // Show suspended error if redirected from dashboard
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('error') === 'suspended') {
        setError('Your account has been suspended. Please contact support.');
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      await signIn(email, password);

      // Send welcome or login notification email (non-blocking)
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        const role = user?.user_metadata?.role || 'customer';
        const name = user?.user_metadata?.full_name || email.split('@')[0];

        // Detect first login: created_at and last_sign_in_at are within 60 seconds
        const createdAt = user?.created_at ? new Date(user.created_at).getTime() : 0;
        const lastSignIn = user?.last_sign_in_at ? new Date(user.last_sign_in_at).getTime() : 0;
        const isFirstLogin = createdAt > 0 && lastSignIn > 0 && Math.abs(lastSignIn - createdAt) < 60000;

        if (isFirstLogin) {
          await supabase.functions.invoke('send-email', {
            body: {
              type: 'welcome',
              to: email,
              data: { email, name, role },
            },
          });
        } else {
          await supabase.functions.invoke('send-email', {
            body: {
              type: 'login',
              to: email,
              data: { email, name, role },
            },
          });
        }

        // Mixpanel: identify user and track login
        if (user) {
          identifyUser(user.id, {
            $email: email,
            $name: name,
            role,
          });
          trackEvent('User Logged In', {
            method: 'email',
            role,
          });
        }
      } catch {
        // Email failure is non-blocking
      }

      router.push('/homepage');
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const appOrigin = typeof window !== 'undefined' ? window.location.origin : (process.env.NEXT_PUBLIC_SITE_URL || 'https://prizzmma.shop');
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${appOrigin}/auth/callback?next=/reset-password`,
      });
      if (error) throw error;
      setForgotSuccess(true);
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Forgot Password View ──────────────────────────────────────────────────
  if (showForgotPassword) {
    return (
      <div className="space-y-5">
        <div>
          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotEmail('');
              setForgotError('');
              setForgotSuccess(false);
            }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Sign In
          </button>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Reset password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your email and we&apos;ll send you a reset link.
          </p>
        </div>

        {forgotSuccess ? (
          <div className="flex flex-col items-center gap-3 px-4 py-6 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Icon name="CheckCircleIcon" size={24} className="text-green-400" />
            </div>
            <p className="text-green-400 font-semibold text-sm">Reset link sent!</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              Check your inbox at <span className="text-foreground font-medium">{forgotEmail}</span> and click the link to reset your password.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotEmail('');
                setForgotSuccess(false);
              }}
              className="mt-2 text-xs text-accent hover:underline"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            {forgotError && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <Icon name="ExclamationCircleIcon" size={16} />
                {forgotError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={e => { setForgotEmail(e.target.value); setForgotError(''); }}
                placeholder="you@example.com"
                className="input-field w-full px-4 py-3 rounded-xl text-sm"
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={forgotLoading}
              className="w-full btn-accent py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {forgotLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}
      </div>
    );
  }

  // ── Login View ────────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your Prizma account</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <Icon name="ExclamationCircleIcon" size={16} />
          {error}
        </div>
      )}

      {/* Email */}
      <div className="space-y-1.5">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</label>
        <input
          type="email"
          value={email}
          onChange={e => { setEmail(e.target.value); setError(''); }}
          placeholder="you@example.com"
          className="input-field w-full px-4 py-3 rounded-xl text-sm"
          required
          autoComplete="email"
        />
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</label>
          <button
            type="button"
            onClick={() => { setShowForgotPassword(true); setForgotEmail(email); }}
            className="text-xs text-accent hover:underline"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            placeholder="Your password"
            className="input-field w-full px-4 py-3 pr-11 rounded-xl text-sm"
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full btn-accent py-3.5 rounded-xl font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Signing in...
          </>
        ) : (
          'Sign In'
        )}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Don&apos;t have an account?{' '}
        <button type="button" onClick={onSwitchToSignup} className="text-accent font-semibold hover:underline">
          Create one
        </button>
      </p>
    </form>
  );
}